/**
 * Focused, professional firm insights.
 *
 * Surfaces the metrics that actually matter for running a law firm:
 * realization rate, collection rate, utilization, AR ageing, DSO — all
 * computed by the reworked ``FirmStatsService`` on the backend.
 *
 * This panel is intentionally narrow. It complements the existing
 * ``FirmStatistics`` component rather than replacing it; users can
 * scroll down for the granular breakdowns they already rely on.
 */
import { useEffect, useState } from "react"
import { AlertCircle, Clock, PercentCircle, Receipt, TrendingUp } from "lucide-react"

import { AgeingBar } from "@/components/ui/ageing-bar"
import { Section } from "@/components/ui/page-shell"
import { Skeleton } from "@/components/ui/skeleton"
import { StatCard } from "@/components/ui/stat-card"
import { useAuth } from "@/hooks/useAuth"
import { useHideAmounts } from "@/hooks/useHideAmounts"
import { statsApi } from "@/services/statistics"

interface Props {
  currency?: string
}

export function FirmInsightsPanel({ currency = "EUR" }: Props) {
  const { user } = useAuth()
  const { hideAmounts } = useHideAmounts()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!user?.firms?.[0]) return
      setLoading(true)
      setError(null)
      try {
        const data = await statsApi.getComprehensiveFirmStats(user.firms[0], {
          period: "month",
          detail: "full",
        })
        if (!cancelled) setStats(data)
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? "Failed to load")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user])

  const fmtCurrency = (v: number | string | undefined | null) => {
    if (hideAmounts) return "•••"
    const n = typeof v === "string" ? parseFloat(v) : v ?? 0
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n)
  }
  const fmtPct = (v: number | undefined | null) =>
    v == null || !Number.isFinite(v) ? "—" : `${Math.round(v)}%`
  const fmtDays = (v: number | undefined | null) =>
    v == null || !Number.isFinite(v) ? "—" : `${Math.round(v)} days`

  const financial = stats?.financial_metrics ?? {}
  const time = stats?.time_metrics ?? {}
  const billing = stats?.billing_metrics ?? {}

  if (error) {
    return (
      <Section title="Firm insights" description="Core KPIs for this month">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      </Section>
    )
  }

  return (
    <Section title="Firm insights">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {loading ? (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[110px] rounded-lg" />
            ))}
          </>
        ) : (
          <>
            <StatCard
              label="Realization"
              value={fmtPct(financial.realization_rate)}
              hint="Billable hours → invoiced"
              icon={<PercentCircle className="h-4 w-4" />}
            />
            <StatCard
              label="Collection"
              value={fmtPct(billing.collection_rate)}
              hint="Invoiced → paid"
              icon={<Receipt className="h-4 w-4" />}
            />
            <StatCard
              label="Utilization"
              value={fmtPct(time.utilization_rate)}
              hint={`${
                time.billable_hours ? Math.round(Number(time.billable_hours)) : 0
              }h billable of ${
                time.total_hours ? Math.round(Number(time.total_hours)) : 0
              }h tracked`}
              icon={<Clock className="h-4 w-4" />}
            />
            <StatCard
              label="Days to pay"
              value={fmtDays(billing.days_sales_outstanding ?? billing.avg_days_to_payment)}
              hint="Avg DSO for this period"
              icon={<TrendingUp className="h-4 w-4" />}
            />
          </>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border bg-background p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="text-sm font-semibold">Accounts receivable ageing</h3>
            <div className="text-xs text-muted-foreground">
              Outstanding: {fmtCurrency(billing.total_outstanding)}
            </div>
          </div>
          {loading || !billing.ageing ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <AgeingBar buckets={billing.ageing} formatAmount={fmtCurrency} />
          )}
        </div>
        <div className="rounded-lg border bg-background p-4">
          <h3 className="mb-3 text-sm font-semibold">Work in progress</h3>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">WIP value</dt>
                <dd className="tabular-nums">{fmtCurrency(financial.wip_value)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">WIP hours</dt>
                <dd className="tabular-nums">
                  {financial.wip_hours
                    ? `${Math.round(Number(financial.wip_hours))}h`
                    : "0h"}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">Billable hours marked</dt>
                <dd className="tabular-nums">
                  {financial.billable_hours
                    ? `${Math.round(Number(financial.billable_hours))}h`
                    : "0h"}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">Of which invoiced</dt>
                <dd className="tabular-nums">
                  {financial.invoiced_hours
                    ? `${Math.round(Number(financial.invoiced_hours))}h`
                    : "0h"}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>
    </Section>
  )
}
